import { useEffect, useState } from "react";
import type { AdminFeatureEntitlementsResponseDto } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminFeatureEntitlements } from "../../shared/api/adminSettings";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const pageLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/integrations", label: "連携設定" }
];

function findEnabled(
  entitlements: AdminFeatureEntitlementsResponseDto | null,
  featureKey: "billingReports" | "billingManagement"
): boolean {
  return entitlements?.items.find((item) => item.featureKey === featureKey)?.enabled ?? false;
}

export function AdminBillingPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [entitlements, setEntitlements] = useState<AdminFeatureEntitlementsResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    let cancelled = false;

    async function loadEntitlements() {
      setLoadState("loading");
      setMessage(null);

      try {
        const nextEntitlements = await fetchAdminFeatureEntitlements(tenantSlug);

        if (cancelled) {
          return;
        }

        setEntitlements(nextEntitlements);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setMessage(getProtectedApiErrorMessage(error, "請求機能の状態取得に失敗しました"));
      }
    }

    void loadEntitlements();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  const billingReportsEnabled = findEnabled(entitlements, "billingReports");
  const billingManagementEnabled = findEnabled(entitlements, "billingManagement");

  let title = "請求";
  let body = [
    "請求機能は有料オプションです。",
    "FamilyAccount 単位の月謝レポートや請求書管理を行う機能です。",
    "利用をご希望の場合は MADAMUH LESSON 運営までお問い合わせください。"
  ];

  if (billingReportsEnabled && !billingManagementEnabled) {
    body = ["請求レポート機能が有効です。実レポート機能は今後実装予定です。"];
  } else if (billingManagementEnabled) {
    body = ["請求管理機能が有効です。請求書作成・入金管理は今後実装予定です。"];
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session} title={title} description="有料機能 / 請求レポート・請求管理の状態を確認します。" links={pageLinks}>
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p style={{ margin: 0 }}>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p style={{ margin: 0 }}>Entry へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p style={{ margin: 0 }}>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p style={{ margin: 0 }}>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p style={{ margin: 0 }}>請求機能の状態を読み込んでいます。</p> : null}
        {message ? <p style={{ margin: 0 }}>{message}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <section
            style={{
              display: "grid",
              gap: 12,
              padding: 16,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: billingReportsEnabled || billingManagementEnabled ? "#f8fafc" : "#fff7ed"
            }}
          >
            <strong>{title}</strong>
            {body.map((line) => (
              <p key={line} style={{ margin: 0 }}>
                {line}
              </p>
            ))}
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              billingReports: {billingReportsEnabled ? "enabled" : "disabled"} / billingManagement:{" "}
              {billingManagementEnabled ? "enabled" : "disabled"}
            </div>
          </section>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
